import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate, useParams } from 'react-router';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormFieldValue } from '../forms/FormFieldValue';
import PageContainer from '../layout/PageContainer';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import VendorForm, { VendorFormState } from './VendorForm';
import { type Vendor, validate as validateVendor } from '../../models/Vendor';
import { GET_VENDOR_BY_ID_QUERY, GET_VENDORS_QUERY } from '../../queries/VendorQueries';
import { UPDATE_VENDOR_MUTATION } from '../../mutations/VendorMutations';

function VendorEditForm({
  initialValues,
  onSubmit,
}: {
  initialValues: Partial<VendorFormState['values']>;
  onSubmit: (id: number, formValues: Partial<VendorFormState['values']>) => Promise<boolean>;
}) {
  const { vendorId } = useParams();
  const navigate = useNavigate();

  const notifications = useNotifications();

  const [formState, setFormState] = useState<VendorFormState>(() => ({
    values: initialValues,
    errors: {},
  }));

  const formValues = formState.values;
  const formErrors = formState.errors;

  const setFormValues = useCallback(
    (newFormValues: Partial<VendorFormState['values']>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    [],
  );

  const setFormErrors = useCallback(
    (newFormErrors: Partial<VendorFormState['errors']>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    [],
  );

  const handleFormFieldChange = useCallback(
    (name: keyof VendorFormState['values'], value: FormFieldValue) => {
      const validateField = async (values: Partial<VendorFormState['values']>) => {
        const { issues } = validateVendor(values);
        setFormErrors({
          ...formErrors,
          [name]: issues?.find((issue) => issue.path?.[0] === name)?.message,
        });
      };

      const newFormValues = { ...formValues, [name]: value };

      setFormValues(newFormValues);
      validateField(newFormValues);
    },
    [formValues, formErrors, setFormErrors, setFormValues],
  );

  const handleFormReset = useCallback(() => {
    setFormValues(initialValues);
  }, [initialValues, setFormValues]);

  const handleFormSubmit = useCallback(async () => {
    const { issues } = validateVendor(formValues);

    if (issues && issues.length > 0) {
      setFormErrors(
        Object.fromEntries(issues.map((issue) => [issue.path?.[0], issue.message])),
      );
      return;
    }

    setFormErrors({});

    const id = +(vendorId || '0');

    if (id > 0) {
      try {
        const success = await onSubmit(id, formValues);

        if (success) {
          notifications.show('Vendor edited successfully.', {
            severity: 'success',
            autoHideDuration: 3000,
          });

          navigate(`/dashboard/vendors/${id}`);
        } else {
          notifications.show(
            'Failed to edit vendor. Reason: Unknown.',
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
      } catch (editError) {
        notifications.show(
          `Failed to edit vendor. Reason: ${(editError as Error).message}`,
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
        throw editError;
      }
    }
  }, [formValues, navigate, notifications, onSubmit, setFormErrors, vendorId]);

  return (
    <VendorForm
      formState={formState}
      onFieldChange={handleFormFieldChange}
      onSubmit={handleFormSubmit}
      onReset={handleFormReset}
      submitButtonLabel="Save"
      backButtonPath={`/dashboard/vendors/${vendorId}`}
    />
  );
}

export default function VendorEdit() {
  const { vendorId } = useParams();

  const [getVendorById] = useLazyQuery(
    GET_VENDOR_BY_ID_QUERY);

  const [updateVendor] = useMutation(
    UPDATE_VENDOR_MUTATION, {
    refetchQueries: [{ query: GET_VENDORS_QUERY }, { query: GET_VENDOR_BY_ID_QUERY, variables: { id: +(vendorId || '0') } }],
  });

  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadData = useCallback(async () => {
    setError(null);

    const id = +(vendorId || '0');

    if (id > 0) {
      setIsLoading(true);

      try {
        const { data, error: getVendorByIdError } = await getVendorById({ variables: { id } });

        setIsLoading(false);
        setError(null);

        if (getVendorByIdError) {
          setError(getVendorByIdError);
        }

        if (data?.vendor) {
          setVendor(data.vendor);
        } else {
          setError({ message: 'No vendor found' } as Error);
        }

      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [getVendorById, vendorId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = useCallback(
    async (id: number, formValues: Partial<VendorFormState['values']>) => {
      setIsLoading(true);

      const { data: result } = await updateVendor({
        variables: {
          id: id,
          input: {
            id: id,
            name: formValues.name || '',
            address: formValues.address || '',
            city: formValues.city || '',
            state: formValues.state || '',
            zip: formValues.zip || '',
            contactId: formValues.contactId || 0
          }
        }
      });

      setIsLoading(false);

      if (result?.updatedVendor) {
        setVendor(result.updatedVendor);
        return true;
      }

      return false;
    },
    [updateVendor],
  );

  const renderEdit = useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return vendor ? (
      <VendorEditForm initialValues={vendor} onSubmit={handleSubmit} />
    ) : null;
  }, [isLoading, error, vendor, handleSubmit]);

  return (
    <PageContainer
      title={`Edit ${vendor?.name}`}
      breadcrumbs={[
        { title: 'Vendors', path: '/dashboard/vendors' },
        { title: `${vendor?.name}`, path: `/dashboard/vendors/${vendorId}` },
        { title: 'Edit' },
      ]}
    >
      <Box sx={{ display: 'flex', flex: 1 }}>{renderEdit}</Box>
    </PageContainer>
  );
}
